import { useCallback, useEffect, useMemo, useState } from 'react'

/**
 * Hook para detectar si una media query coincide
 * @param query - Media query string (ej: '(min-width: 768px)')
 * @param defaultValue - Valor por defecto antes de que se monte el componente
 * @returns boolean indicando si la media query coincide
 */
export function useMediaQuery(query: string, defaultValue: boolean = false): boolean {
  // Función para obtener el valor actual
  const getMatches = useCallback(
    (query: string): boolean => {
      // Prevenir error en SSR
      if (typeof window === 'undefined') {
        return defaultValue
      }
      return window.matchMedia(query).matches
    },
    [defaultValue]
  )

  const [matches, setMatches] = useState<boolean>(() => getMatches(query))

  // Manejar cambios en la media query
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const matchMedia = window.matchMedia(query)

    // Función para manejar cambios
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setMatches(e.matches)
    }

    // Actualizar el estado inicial
    handleChange(matchMedia)

    // Método moderno (addEventListener)
    if (matchMedia.addEventListener) {
      matchMedia.addEventListener('change', handleChange)
      return () => matchMedia.removeEventListener('change', handleChange)
    }
    // Método legacy (addListener) para navegadores antiguos
    else if (matchMedia.addListener) {
      matchMedia.addListener(handleChange)
      return () => matchMedia.removeListener(handleChange)
    }
  }, [query, getMatches])

  return matches
}

/**
 * Breakpoints predefinidos para la aplicación
 */
export const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
} as const

type BreakpointKey = keyof typeof breakpoints

/**
 * Hook para detectar el breakpoint actual
 * @returns El breakpoint actual
 */
export function useBreakpoint(): BreakpointKey {
  const keys = Object.keys(breakpoints) as BreakpointKey[]

  // Crear media queries para cada breakpoint
  const mediaQueries = useMemo(() => {
    return keys.map((key, index) => {
      const minWidth = breakpoints[key]
      const maxWidth = index < keys.length - 1 ? breakpoints[keys[index + 1]] - 1 : null

      if (maxWidth) {
        return `(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`
      }
      return `(min-width: ${minWidth}px)`
    })
  }, [keys])

  // Verificar cada media query
  //   const matchResults = mediaQueries.map((query) => useMediaQuery(query))

  //   const matchResults = useMemo(() => {
  //     return mediaQueries.map((query) => useMediaQuery(query))
  //   }, [mediaQueries])

  // Verificar cada media query usando un array de hooks
  //   const matchResults: boolean[] = []
  //   for (let i = 0; i < mediaQueries.length; i++) {
  //     // eslint-disable-next-line react-hooks/rules-of-hooks
  //     matchResults.push(useMediaQuery(mediaQueries[i]))
  //   }

  //
  //   const matchResults = useMemo(() => {
  //     // eslint-disable-next-line react-hooks/rules-of-hooks
  //     return mediaQueries.map((query) => useMediaQuery(query))
  //   }, [mediaQueries])

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const matchResults = mediaQueries.map((query) => useMediaQuery(query))

  // Encontrar el breakpoint actual (el último que coincide)
  const currentBreakpoint = useMemo(() => {
    let current: BreakpointKey = 'xs'

    keys.forEach((key, index) => {
      if (matchResults[index]) {
        current = key
      }
    })

    return current
  }, [keys, matchResults])

  return currentBreakpoint
}

/**
 * Hook helpers para breakpoints específicos
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)')
}

export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
}

export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)')
}

export function useIsLargeDesktop(): boolean {
  return useMediaQuery('(min-width: 1280px)')
}

/**
 * Hook para detectar orientación del dispositivo
 */
export function useOrientation(): 'portrait' | 'landscape' {
  const isPortrait = useMediaQuery('(orientation: portrait)')
  return isPortrait ? 'portrait' : 'landscape'
}

/**
 * Hook para detectar preferencia de esquema de color
 */
export function usePrefersDarkMode(): boolean {
  return useMediaQuery('(prefers-color-scheme: dark)')
}

/**
 * Hook para detectar si el usuario prefiere movimiento reducido
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)')
}

/**
 * Hook para obtener información completa sobre el viewport
 */
export function useViewport() {
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    // Debounce para mejorar performance
    let timeoutId: NodeJS.Timeout
    const debouncedHandleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleResize, 150)
    }

    window.addEventListener('resize', debouncedHandleResize)

    // Llamar inmediatamente para obtener valores iniciales
    handleResize()

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      clearTimeout(timeoutId)
    }
  }, [])

  const breakpoint = useBreakpoint()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const isDesktop = useIsDesktop()
  const orientation = useOrientation()

  return {
    ...viewport,
    breakpoint,
    isMobile,
    isTablet,
    isDesktop,
    orientation
  }
}
